import { useMemo, useReducer, useEffect } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { TimeSlot, DateUtils } from "shared/utils/date";
import { filterActiveBookings } from "shared/utils/filter-booking";
import { OrganizationHelpers } from "shared/utils/organization";
import BookFreeSpace from "assets/icons/BookFreeSpace.svg";
import { AreaType } from "api/schema";
import { useMyActiveBookings } from "api/queries/bookings/myActiveBookings";
import { useUnavailableTimePeriods } from "api/queries/bookings/unavailableTimePeriods";
import { useCreateFreeSpace } from "api/mutations/booking/createFreeSpaceBooking";
import { useBuildingStructureForFreeSpace } from "api/queries/building/buildingStructureForFreeSpace";
import { useSharedData } from "shared/state";
import { useTranslation } from "react-i18next";
import { initialState, reducer, Actions, BookFreeSpaceContext } from "./state";
import {
  DialogHeader,
  TimePickers,
  DatePicker,
  DialogWrapper,
  ErrorMessage,
  PeriodPicker,
  SelectOptions,
} from "../components";
import {
  getBuildingOptions,
  getFreeSpaceAreas,
  getFloorOptions,
} from "../../../utils/dropdown";

export default function BookFreeSpaceModal() {
  const shared = useSharedData();
  const modalItem = shared?.state?.modalItem as AreaType | undefined;
  const [state, dispatch] = useReducer(reducer, initialState);
  const context = useMemo(() => ({ state, dispatch, Actions }), [state]);
  const hourlyBookingFlag =
    shared.state.myOrganization?.enabledFeatures.includes("hourly_booking");
  const { t } = useTranslation();

  const createBooking = useCreateFreeSpace();

  const minmax = useMemo<TimeSlot>(() => {
    if (shared.state.myOrganization) {
      return [
        shared.state.myOrganization.workingHoursStart,
        shared.state.myOrganization.workingHoursEnd,
      ].map((value) =>
        OrganizationHelpers.parseOrganizationTime(value.toString()),
      ) as TimeSlot;
    }
    return [0, 0];
  }, [shared.state.myOrganization]);

  useEffect(() => {
    dispatch(Actions.setHoursRange(minmax));
  }, [minmax]);

  useEffect(() => {
    dispatch(Actions.selectDate(shared.state.modalInitialDate));
  }, [shared.state.modalInitialDate]);

  useEffect(() => {
    if (shared.state.selectedBuilding) {
      if (!state.selectedBuilding) {
        dispatch(Actions.selectBuilding(shared.state.selectedBuilding));
      } else if (typeof modalItem !== "undefined") {
        const building = shared.state.buildings.find(
          (b) => b.id === modalItem?.floor?.building?.id,
        );
        if (state.selectedBuilding.id !== building?.id) {
          dispatch(Actions.selectBuilding(building));
        }
      }
    }
  }, [shared.state.selectedBuilding, modalItem]);

  const { loading: loadingFloors } = useBuildingStructureForFreeSpace(
    (data) => {
      dispatch(
        Actions.setFloors(
          data?.floors.filter(
            (e) =>
              e.floorType === "mixed" &&
              e.areas.some((el) => el.areaType === "free_spaces_room"),
          ),
        ),
      );
    },
    {
      building: state.selectedBuilding,
      date: state.selectedDate,
    },
  );

  useEffect(() => {
    if (state.floors) {
      if (typeof modalItem !== "undefined") {
        const floor = state.floors.find((f) => f.id === modalItem?.floor?.id);
        if (state.selectedFloor?.id !== floor?.id) {
          dispatch(Actions.selectFloor(floor));
        }
      }
    }
  }, [state.floors, modalItem]);

  useEffect(() => {
    if (state.selectedFloor) {
      dispatch(
        Actions.setFreeSpace(
          (state.selectedFloor?.areas || []).filter(
            (e) => e.areaType === "free_spaces_room",
          ),
        ),
      );
    }
  }, [state.selectedFloor]);

  useEffect(() => {
    if (state.freeSpaces) {
      if (typeof modalItem !== "undefined") {
        const room = state.freeSpaces.find((r) => r.id === modalItem?.id);
        if (state.selectedFreeSpace?.id !== room?.id) {
          dispatch(Actions.selectFreeSpace(room));
        }
      }
    }
  }, [state.freeSpaces, modalItem]);

  const isFloorDisabled = !(state.selectedDate && state.selectedBuilding);
  const isAreaDisabled = !state.selectedFloor;
  const isTimePeriodDisabled = !state.selectedFreeSpace;

  const { refetch: refetchTimePeriods } = useUnavailableTimePeriods(
    (data) => {
      const combinedData = [
        ...(shared.state.myBookedSlots ?? []),
        ...OrganizationHelpers.getUnavailableTimePeriods(data),
      ].sort((a, b) => a - b);

      const uniqueSlots = Array.from(new Set(combinedData));
      dispatch(Actions.setUnavailableSlots(uniqueSlots));
    },
    {
      date: state.selectedDate,
      areaId: state.selectedFreeSpace?.id ?? "",
      skip: isTimePeriodDisabled || !Array.isArray(shared.state.myBookedSlots),
    },
  );

  const { refetch: refetchActiveBookings } = useMyActiveBookings(
    (data) => {
      const filteredByFreeSpace = filterActiveBookings(
        data,
        "free_spaces_room",
      );
      shared.dispatch(shared.Actions.setMyBookedSlots(filteredByFreeSpace));
    },
    {
      start: state.selectedDate,
      end: state.selectedDate,
    },
  );

  useEffect(() => {
    refetchActiveBookings().then(() => {
      shared.dispatch(shared.Actions.setLoading(false));
    });
  }, [state.selectedDate]);

  useEffect(() => {
    refetchTimePeriods();
  }, [state.unavailableSlots]);

  return (
    <BookFreeSpaceContext.Provider value={context}>
      <DialogWrapper open>
        <DialogHeader
          icon={BookFreeSpace}
          title={t("Home.ActiveBooking.Booking.Popup.FreeSpace.Title")}
          subheader={t("Home.ActiveBooking.Booking.Popup.SubTitle")}
        />
        <DialogContent>
          <DatePicker
            initialValue={state.selectedDate}
            onChange={(value) => dispatch(Actions.selectDate(value))}
          />
          <SelectOptions
            placeholder={t("Home.Buildings.Select") as string}
            notAvailablePlaceholder={t("Home.Buildings.NotAvailable") as string}
            state={shared.state.buildings}
            value={state.selectedBuilding?.id ?? ""}
            setValue={(val) => dispatch(Actions.selectBuilding(val))}
            getOptions={() => getBuildingOptions(shared.state)}
          />

          <SelectOptions
            placeholder={t("Filter.Floors.Select") as string}
            notAvailablePlaceholder={t("Filter.Floors.NoFloors") as string}
            disabled={isFloorDisabled}
            state={state.floors}
            isLoading={loadingFloors}
            value={state?.selectedFloor?.id ?? ""}
            setValue={(val) => dispatch(Actions.selectFloor(val))}
            getOptions={() => getFloorOptions(state)}
          />

          <SelectOptions
            placeholder={t(["Filter.Area.Select"]) as string}
            notAvailablePlaceholder={t(["Filter.Area.NoArea"]) as string}
            disabled={isAreaDisabled}
            state={state.freeSpaces}
            value={state?.selectedFreeSpace?.id ?? ""}
            setValue={(val) => dispatch(Actions.selectFreeSpace(val))}
            getOptions={() => getFreeSpaceAreas(state)}
          />

          <Typography variant="caption" display="block" gutterBottom>
            {t(["Time.Slots.selection"])}
          </Typography>
          <PeriodPicker
            bookingType="desk"
            unavailableSlots={state.unavailableSlots}
            selectedStart={state.hoursStart ?? 0}
            selectedEnd={state.hoursEnd ?? 0}
            onSelect={(start, end) => {
              dispatch(Actions.setHoursRange([start, end]));
            }}
          />
          {hourlyBookingFlag && (
            <TimePickers
              isDisabled={isTimePeriodDisabled}
              selectedDate={state.selectedDate}
              hoursStart={state.hoursStart}
              hoursEnd={state.hoursEnd}
              onChange={(start, end) => {
                dispatch(Actions.setHoursRange([start, end]));
              }}
              setHourRange={(timeSlot) =>
                dispatch(Actions.setHoursRange(timeSlot))
              }
              unavailableSlots={state.unavailableSlots}
              minmax={minmax}
            />
          )}
          {state.hoursStart === state.hoursEnd && <ErrorMessage />}
        </DialogContent>

        <DialogActions>
          <Button
            color="primary"
            disabled={
              !state.selectedFreeSpace?.id ||
              state.hoursStart === state.hoursEnd
            }
            onClick={() => {
              createBooking({
                variables: {
                  areaId: state.selectedFreeSpace?.id ?? "",
                  start: DateUtils.setFullUTCHour(
                    state.selectedDate,
                    state.hoursStart ?? 0,
                  ),
                  end: DateUtils.setFullUTCHour(
                    state.selectedDate,
                    state.hoursEnd ?? 0,
                  ),
                },
              })
                .then(() => {
                  shared.dispatch(shared.Actions.setLoading(true));
                  shared.dispatch(
                    shared.Actions.notifySuccess("Free space has been booked."),
                  );
                })
                .catch((error) => {
                  shared.dispatch(
                    shared.Actions.notifyError(
                      error?.message ||
                        "Free space has not been booked. Please try again.",
                    ),
                  );
                });
              shared.dispatch(shared.Actions.openModal());
            }}
          >
            {t(["Routes.Home.Book.Confirm"])}
          </Button>
        </DialogActions>
      </DialogWrapper>
    </BookFreeSpaceContext.Provider>
  );
}
